import React, { useContext, useState } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import GridContentTemplate from '../../../../components/templates/GridContentTemplate';
import useGridDataWithRTK from '../../../../hooks/useGridData/useGridDataWithRTK';
import {
	SUBPRODUCT_CREATE_PATH,
	SUBPRODUCT_DETAIL_PATH,
} from '../../../../routes/constants/urls';
import userData from './gridData';
import {
	useGetSubproductsQuery,
	useUpdateSubproductMutation,
} from '../../../../store/apis/subproduct';
import Button from '../../../../components/atoms/Button';
import { ToastContext } from '../../../../contexts/Toast';
import Alert from '../../../../components/atoms/Alert';
import { CustomErrorType } from '../../../../store/apis/@types';
import { PRODUCT_STATE_VISIBLE_VALUE } from '../../../../utils/const';

const ButtonContainer = styled.div`
	display: flex;
	align-items: center;
	gap: 8px;
`;

enum AlertState {
	NULL,
	UPDATE_STATE,
}

function SubproductPage() {
	const setToast = useContext(ToastContext);

	const { formInitialValues, colsList, forms } = userData;
	const navigate = useNavigate();
	const [alertState, setAlertState] = useState<AlertState>(AlertState.NULL);
	const [updateStateBody, setUpdateStateBody] = useState<{
		id: number;
		state: string;
	} | null>(null);

	const [updateSubproductState] = useUpdateSubproductMutation();

	const {
		rowData,
		totalCount,
		cols,
		page,
		pageSize,
		setGridApi,
		handlePageChange,
		handlePageSizeChange,
		handleSearchSubmit,
		handleDetailClick,
		refetch,
	} = useGridDataWithRTK({
		useQuery: useGetSubproductsQuery,
		formInitialValues,
		colsList,
		detailPath: SUBPRODUCT_DETAIL_PATH,
	});

	const goTo = (route: string) => {
		navigate(route);
	};

	const handleAlertClose = () => {
		setAlertState(AlertState.NULL);
	};

	const handleUpdateState = async () => {
		if (updateStateBody !== null) {
			const result = await updateSubproductState({
				subproductId: updateStateBody.id,
				body: {
					productViewable:
						updateStateBody.state === PRODUCT_STATE_VISIBLE_VALUE,
				},
			});

			if ('error' in result) {
				setToast(
					'error',
					(result.error as CustomErrorType).data.translate ||
						(result.error as CustomErrorType).data.message,
				);
			} else {
				setToast('info', '상태변경이 완료되었습니다.');
				handleAlertClose();
				refetch();
			}

			setUpdateStateBody(null);
		}
	};

	const handleCreateClick = () => {
		navigate(SUBPRODUCT_CREATE_PATH);
	};

	const renderAlert = () => {
		if (alertState === AlertState.UPDATE_STATE) {
			return (
				<Alert
					title="상품 상태를 변경하시겠습니까?"
					closeButtonClick={handleAlertClose}
					onConfirmButtonText="변경하기"
					onConfirmButtonClick={handleUpdateState}
					isVisible
				/>
			);
		}
		return null;
	};

	return (
		<>
			<GridContentTemplate
				gridRef={setGridApi}
				mainTitle="하위상품 관리"
				gridTitle="하위상품 목록"
				totalCount={totalCount}
				currentPage={page}
				currentPageSize={pageSize}
				onPageChange={handlePageChange}
				onPageSizeChange={handlePageSizeChange}
				rowData={rowData}
				cols={cols}
				formInitialValues={formInitialValues}
				forms={forms}
				onSearchSubmit={handleSearchSubmit}
				onRowClicked={handleDetailClick}
				rightAccessory={
					<ButtonContainer onClick={() => goTo(SUBPRODUCT_CREATE_PATH)}>
						<Button onClick={handleCreateClick} size="small">
							하위 상품 등록
						</Button>
					</ButtonContainer>
				}
			/>
			{renderAlert()}
		</>
	);
}

export default SubproductPage;
